import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchBreaks,
  fetchTimeCut,
  updateBreaks,
  updateTimeCut,
  fetchPdnsDisplay,
  fetchCompaniesPdns,
  updateCompanyPdns,
  fetchSundayHoursDisplay,
  fetchCompaniesSundayHours,
  updateCompanySundayHours,
} from "../services/timeManagementService";

const DEFAULT_BREAKS = [
  {
    name: "display_breaks",
    title: "Afficher les pauses",
    checked: false,
    desc: "Les pauses seront incluses en tant que lignes distinctes dans le fichier export.",
  },
  {
    name: "exclude_breaks",
    title: "Exclure les pauses",
    checked: false,
    desc: "Les pauses seront exclues du fichier export.",
  },
  {
    name: "add_breaks_to_hours_worked",
    title: "Ajouter les pauses aux heures travaillées",
    checked: false,
    desc: "Les pauses seront intégrées directement au total des heures travaillées dans le fichier export.",
  },
  {
    name: "add_breaks_to_hours_worked_no_line",
    title: "Ajouter les pauses aux heures travaillées (sans ligne pause)",
    checked: false,
    desc: "Les pauses seront intégrées au total des heures travaillées sans ligne de pause distincte dans le fichier export.",
  },
];

function useSettingsTimeManagement() {
  const [timeCut, setTimeCut] = useState(0);
  const [breaks, setBreaks] = useState(DEFAULT_BREAKS);
  const [isWaiting, setIsWaiting] = useState(false);
  const [isPdnsDisplay, setIsPdnsDisplay] = useState(false);
  const [companiesPdns, setCompaniesPdns] = useState([]);
  const [isSundayHoursDisplay, setIsSundayHoursDisplay] = useState(false);
  const [companiesSundayHours, setCompaniesSundayHours] = useState([]);

  const timeCutTimeoutRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      setIsWaiting(true);

      try {
        const timeCutRes = await fetchTimeCut();
        if (timeCutRes && typeof timeCutRes.timeCut !== "undefined") {
          setTimeCut(timeCutRes.timeCut);
        }

        const breaksRes = await fetchBreaks();
        if (breaksRes && breaksRes.breaks) {
          setBreaks((prev) =>
            prev.map((b) => ({
              ...b,
              checked: b.name === breaksRes.breaks,
            }))
          );
        }

        // Load PDNS display setting and companies
        const pdnsDisplayRes = await fetchPdnsDisplay();
        if (pdnsDisplayRes && typeof pdnsDisplayRes.isPdnsDisplay !== "undefined") {
          setIsPdnsDisplay(pdnsDisplayRes.isPdnsDisplay);
        }

        // Only fetch companies if PDNS display is enabled
        if (pdnsDisplayRes?.isPdnsDisplay) {
          const companiesPdnsRes = await fetchCompaniesPdns();
          if (companiesPdnsRes && companiesPdnsRes.companies) {
            setCompaniesPdns(companiesPdnsRes.companies);
          }
        }

        // Load Sunday Hours display setting and companies
        const sundayHoursDisplayRes = await fetchSundayHoursDisplay();
        if (sundayHoursDisplayRes && typeof sundayHoursDisplayRes.isSundayHoursDisplay !== "undefined") {
          setIsSundayHoursDisplay(sundayHoursDisplayRes.isSundayHoursDisplay);
        }

        // Only fetch companies if Sunday Hours display is enabled
        if (sundayHoursDisplayRes?.isSundayHoursDisplay) {
          const companiesSundayHoursRes = await fetchCompaniesSundayHours();
          if (companiesSundayHoursRes && companiesSundayHoursRes.companies) {
            setCompaniesSundayHours(companiesSundayHoursRes.companies);
          }
        }
      } catch (error) {
        console.error("Erreur lors du chargement des paramètres :", error);
      } finally {
        setIsWaiting(false);
      }
    };

    loadData();
  }, []);

  const handleTimeCutChange = useCallback((e) => {
    const newValue = e.target.value;
    setTimeCut(newValue);

    if (timeCutTimeoutRef.current) {
      clearTimeout(timeCutTimeoutRef.current);
    }

    timeCutTimeoutRef.current = setTimeout(async () => {
      try {
        await updateTimeCut(newValue);
      } catch (error) {
        console.error("Erreur lors de la mise à jour du timeCut :", error);
      }
    }, 1000);
  }, []);

  const handleBreaksChange = useCallback(
    async (e) => {
      const selectedBreak = e.target.value;

      setBreaks((prev) =>
        prev.map((b) => ({
          ...b,
          checked: b.name === selectedBreak,
        }))
      );

      try {
        await updateBreaks(selectedBreak);
      } catch (error) {
        console.error("Erreur lors de la mise à jour des pauses :", error);
      }
    },
    [setBreaks]
  );

  // Handler for PDNS checkbox changes
  const handlePdnsChange = useCallback(
    async (updatedList) => {
      // Find the changed company by comparing with current state
      const changedCompany = updatedList.find((item) => {
        const currentCompany = companiesPdns.find((c) => c._id === item.value);
        return currentCompany && currentCompany.isPdns !== item.checked;
      });

      if (!changedCompany) return;

      // Update local state immediately for responsive UI
      setCompaniesPdns((prev) =>
        prev.map((c) =>
          c._id === changedCompany.value
            ? { ...c, isPdns: changedCompany.checked }
            : c
        )
      );

      try {
        await updateCompanyPdns(changedCompany.value, changedCompany.checked);
      } catch (error) {
        console.error("Erreur lors de la mise à jour PDNS :", error);
        // Revert on error
        setCompaniesPdns((prev) =>
          prev.map((c) =>
            c._id === changedCompany.value
              ? { ...c, isPdns: !changedCompany.checked }
              : c
          )
        );
      }
    },
    [companiesPdns]
  );

  // Handler for Sunday Hours checkbox changes
  const handleSundayHoursChange = useCallback(
    async (updatedList) => {
      // Find the changed company by comparing with current state
      const changedCompany = updatedList.find((item) => {
        const currentCompany = companiesSundayHours.find((c) => c._id === item.value);
        return currentCompany && currentCompany.isSundayHours !== item.checked;
      });

      if (!changedCompany) return;

      // Update local state immediately for responsive UI
      setCompaniesSundayHours((prev) =>
        prev.map((c) =>
          c._id === changedCompany.value
            ? { ...c, isSundayHours: changedCompany.checked }
            : c
        )
      );

      try {
        await updateCompanySundayHours(changedCompany.value, changedCompany.checked);
      } catch (error) {
        console.error("Erreur lors de la mise à jour Heures Dimanche :", error);
        // Revert on error
        setCompaniesSundayHours((prev) =>
          prev.map((c) =>
            c._id === changedCompany.value
              ? { ...c, isSundayHours: !changedCompany.checked }
              : c
          )
        );
      }
    },
    [companiesSundayHours]
  );

  return {
    timeCut,
    breaks,
    isWaiting,
    handleTimeCutChange,
    handleBreaksChange,
    isPdnsDisplay,
    companiesPdns,
    handlePdnsChange,
    isSundayHoursDisplay,
    companiesSundayHours,
    handleSundayHoursChange,
  };
}

export default useSettingsTimeManagement;
